import { Box, TableCell } from "@mui/material";
import { DotLegalLink, ToDto } from "@dotlegal/dotlegal-ui-components";
import { EntityFieldType } from "@common/entityField.types";
import DynamicCellValueRenderer, { DynamicCellValueRendererProps } from "./DynamicCellValueRenderer.tsx";

interface DynamicCellRendererProps extends Omit<DynamicCellValueRendererProps, "showFullContent"> {
    linkTo?: ToDto | string;
    // For the column that names the row, which the overviews set in bold whether or not it links anywhere.
    bold?: boolean;
    // The user friendly id is typed as a number, but it identifies the row rather than measuring anything, so it is
    // laid out like text.
    isIdentifier?: boolean;
}

function isNumeric(type: EntityFieldType) {
    return type === EntityFieldType.Number || type === EntityFieldType.Decimal || type === EntityFieldType.Amount;
}

function getCellWidth(type: EntityFieldType) {
    switch (type) {
        // A width keeps numeric columns narrow so the right-aligned values stay close to their header, as the
        // table otherwise spreads its spare width over every column. Not wrapping lets a longer value still widen it.
        case EntityFieldType.Amount:
            return { width: "150px", whiteSpace: "nowrap" };
        case EntityFieldType.Number:
        case EntityFieldType.Decimal:
            return { width: "110px", whiteSpace: "nowrap" };
        case EntityFieldType.Html:
        case EntityFieldType.MultiLineText:
            return { maxWidth: "250px", width: "250px", minWidth: "250px" };
        // Classifications render a name, an icon and a link side by side, which wraps into unreadable lines below
        // this width.
        case EntityFieldType.LegalEntityClassifications:
            return { minWidth: "350px" };
        // The risk area name shrinks before its level chip does, so without room the name is cut down to a few
        // letters and the reader cannot tell which area the level belongs to.
        case EntityFieldType.RiskAreas:
            return { minWidth: "200px" };
        // The progress bar takes the width it is given, so a fixed width keeps it the same on every row.
        case EntityFieldType.ProcessingActivityProgress:
            return { maxWidth: "270px", width: "270px", minWidth: "270px" };
        default:
            return undefined;
    }
}

export function DynamicCellRenderer({ field, entityId, linkTo, bold, isIdentifier, riskEntityType, legalEntityScope }: DynamicCellRendererProps) {
    const isRightAligned = isNumeric(field.type) && !isIdentifier;
    const content = <DynamicCellValueRenderer field={field} entityId={entityId} riskEntityType={riskEntityType} legalEntityScope={legalEntityScope} />;
    const cellWidth = isIdentifier ? undefined : getCellWidth(field.type);

    // Set on the text itself rather than the cell: the link forces its own weight on the anchor, and only an element
    // inside it can be heavier.
    const styledContent = bold ? (
        <Box component={"span"} sx={{ fontWeight: 600 }}>
            {content}
        </Box>
    ) : (
        content
    );

    return (
        <TableCell sx={cellWidth} align={isRightAligned ? "right" : "left"}>
            {linkTo ? (
                <DotLegalLink bold linkColor="primary" to={linkTo} stopPropagation>
                    {styledContent}
                </DotLegalLink>
            ) : (
                styledContent
            )}
        </TableCell>
    );
}

export default DynamicCellRenderer;
